/**
 * Spam / abuse report — Signal-parity "deniable to the server" report token.
 *
 * Threat model
 * ============
 *
 * A user reports a peer for spam/abuse/harassment/CSAM/etc. The report
 * must reach the server with enough information for the server to act on
 * the abusive account, but the report MUST NOT leak the offending message
 * body to the server. Concretely the server learns:
 *
 *   - reportedUserId   (the account being reported)
 *   - reporterUserId   (signed; binds the report to a real account)
 *   - category         (a CATEGORICAL enum — NEVER free-text — so the
 *                       reporter cannot accidentally paste message content
 *                       into a "reason" field)
 *   - messageIdHash    (SHA-256 of the offending messageId UTF-8 bytes,
 *                       NOT the messageId itself; the server cannot index
 *                       this back to a message body)
 *   - reportedAt       (LOCAL reporter clock; the server does not supply
 *                       this — prevents the server from rewriting the
 *                       report's timestamp)
 *
 * Properties
 * ----------
 *
 *   1. The signature is Ed25519 over a DETERMINISTIC canonical byte layout
 *      (NOT JSON) using the reporter's identity private key. The server
 *      cannot forge a report because it does not have the user's private
 *      key. JSON encoding noise cannot vary the signed surface.
 *
 *   2. There is NO "free-text reason" field on the wire. The SpamReportPayload
 *      type enforces this at the type level — SpamReportSelfTest pins the
 *      exact field set so an accidental addition fails the suite.
 *
 *   3. There is NO field for the raw `messageId` — only its 32-byte SHA-256
 *      digest. The digest is computed LOCALLY before the report leaves the
 *      device.
 *
 *   4. Cooldown is enforced LOCALLY (SpamReportStore) — defence-in-depth
 *      against a hostile UI flooding the abuse pipeline.
 *
 * Wire format (NOT the on-the-wire E2EE envelope — abuse reports travel
 * through the server's /abuse/report endpoint, currently unwired):
 *
 *   kaether:spam:report:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "payload": { "schema": 1,
 *                  "reportedUserId": "alice",
 *                  "reporterUserId": "bob",
 *                  "category": "harassment",
 *                  "messageIdHash": "<base64-of-32-bytes>",
 *                  "reportedAt": 1700000000 },
 *     "signature": "<base64-of-64-bytes>" }
 *
 * `signature` is Ed25519 over the *canonical bytes* defined in
 * `SpamReportCodec.canonicalBytes(payload)` — NOT over the JSON.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/SpamReport.swift`.
 */

// ---------------------------------------------------------------------------
// SpamReportCategory
// ---------------------------------------------------------------------------

/**
 * Categorical reason a peer is being reported. Free-text reasons are
 * deliberately excluded from the wire payload — see file header.
 *
 * Mirrors `SpamReportCategory` in `kaether_for_ios/Stoat/E2EE/SpamReport.swift`.
 */
export type SpamReportCategory =
  | "spam"
  | "harassment"
  | "impersonation"
  | "illegalContent"
  | "csam"
  | "other";

export const SPAM_REPORT_CATEGORIES: readonly SpamReportCategory[] = [
  "spam",
  "harassment",
  "impersonation",
  "illegalContent",
  "csam",
  "other",
] as const;

// ---------------------------------------------------------------------------
// SpamReportPayload
//
// IMPORTANT: there MUST be NO `reason: string`, `reasonText: string`,
// `freeText: string`, `comment: string`, or any other free-text field here.
// SpamReportSelfTest checks the exact field set at runtime via Object.keys().
// ---------------------------------------------------------------------------

/** Schema version constant. Increment when the wire shape changes. */
export const SPAM_REPORT_SCHEMA_VERSION = 1 as const;

/**
 * The signed, serialisable payload for a spam report.
 *
 * TYPE-LEVEL INVARIANT:
 *   - NO `reason: string` / `reasonText` / `freeText` / `comment` field.
 *     Category is a closed enum (`SpamReportCategory`) only.
 *   - `messageIdHash` is `Uint8Array` (32 bytes, SHA-256 output).
 *     The raw `messageId: string` MUST NOT appear here.
 *
 * Mirrors `SpamReportPayload` in `kaether_for_ios/Stoat/E2EE/SpamReport.swift`.
 */
export interface SpamReportPayload {
  /** Always `SPAM_REPORT_SCHEMA_VERSION` (1) for this version. */
  readonly schema: number;
  /** The peer being reported. Required so the server can act on the account. */
  readonly reportedUserId: string;
  /** The reporter's userId. Bound to the signature for accountability. */
  readonly reporterUserId: string;
  /** Categorical reason — NEVER free text. */
  readonly category: SpamReportCategory;
  /**
   * 32-byte SHA-256 of the offending messageId UTF-8 bytes.
   * NOT the messageId itself; the server cannot index this back to a body.
   */
  readonly messageIdHash: Uint8Array;
  /**
   * LOCAL reporter clock — Unix seconds. The server does NOT supply this
   * timestamp; allowing it to do so would let a hostile server rewrite
   * the report's claimed-at time.
   */
  readonly reportedAt: number;
}

/**
 * A signed spam report: payload + 64-byte Ed25519 signature.
 * Mirrors `SpamReport` in `kaether_for_ios/Stoat/E2EE/SpamReport.swift`.
 */
export interface SpamReport {
  readonly payload: SpamReportPayload;
  /** 64-byte Ed25519 signature over `SpamReportCodec.canonicalBytes(payload)`. */
  readonly signature: Uint8Array;
}

// ---------------------------------------------------------------------------
// SpamReportError
// ---------------------------------------------------------------------------

export type SpamReportErrorKind =
  | { readonly tag: "malformed"; readonly detail: string }
  | { readonly tag: "unsupportedSchema"; readonly version: number }
  | { readonly tag: "invalidSignature" }
  | { readonly tag: "invalidMessageIdHashLength"; readonly length: number }
  | { readonly tag: "emptyReportedUserId" }
  | { readonly tag: "emptyReporterUserId" }
  | { readonly tag: "reporterEqualsReported" };

export class SpamReportError extends Error {
  constructor(public readonly kind: SpamReportErrorKind) {
    super(SpamReportError.message(kind));
    this.name = "SpamReportError";
  }

  private static message(kind: SpamReportErrorKind): string {
    switch (kind.tag) {
      case "malformed":
        return `malformed spam report: ${kind.detail}`;
      case "unsupportedSchema":
        return `unsupported spam report schema version: ${kind.version}`;
      case "invalidSignature":
        return "invalid Ed25519 signature";
      case "invalidMessageIdHashLength":
        return `messageIdHash must be 32 bytes, got ${kind.length}`;
      case "emptyReportedUserId":
        return "reportedUserId must not be empty";
      case "emptyReporterUserId":
        return "reporterUserId must not be empty";
      case "reporterEqualsReported":
        return "reporterUserId must not equal reportedUserId (cannot report yourself)";
    }
  }
}

// ---------------------------------------------------------------------------
// SpamReportCodec
// ---------------------------------------------------------------------------

// JSON shape for the over-the-wire representation.
// messageIdHash is serialised as standard base64 (not base64url) for the JSON
// field — this mirrors the iOS `Data.base64EncodedString()` behaviour.
interface SpamReportPayloadJson {
  schema: number;
  reportedUserId: string;
  reporterUserId: string;
  category: SpamReportCategory;
  messageIdHash: string; // base64
  reportedAt: number;
}

interface SpamReportJson {
  payload: SpamReportPayloadJson;
  signature: string; // base64
}

/**
 * Codec for the `kaether:spam:report:v1:` wire format.
 * Mirrors `SpamReportCodec` in `kaether_for_ios/Stoat/E2EE/SpamReport.swift`.
 */
export const SpamReportCodec = {
  PREFIX: "kaether:spam:report:v1:" as const,

  // -------------------------------------------------------------------------
  // Cheap prefix check
  // -------------------------------------------------------------------------

  /** Cheap prefix check — does not validate the payload. */
  isReport(s: string): boolean {
    return s.startsWith(SpamReportCodec.PREFIX);
  },

  // -------------------------------------------------------------------------
  // Canonical signed surface
  // -------------------------------------------------------------------------

  /**
   * Canonical signed surface (deterministic byte layout, NOT JSON):
   *
   *   schema(8 LE) ‖ reportedUserId(UTF-8) ‖ 0x1F
   *   ‖ reporterUserId(UTF-8) ‖ 0x1F
   *   ‖ category(UTF-8) ‖ 0x1F
   *   ‖ messageIdHash(32) ‖ 0x1F
   *   ‖ reportedAt(8 LE)
   *
   * The 0x1F unit separators prevent splice attacks between adjacent
   * variable-length fields.
   *
   * Mirrors `SpamReportCodec.canonicalBytes` in iOS.
   */
  canonicalBytes(p: SpamReportPayload): Uint8Array {
    const enc = new TextEncoder();
    const reportedUserIdBytes = enc.encode(p.reportedUserId);
    const reporterUserIdBytes = enc.encode(p.reporterUserId);
    const categoryBytes = enc.encode(p.category);
    const SEP = 0x1f;

    // Total length:
    //   8 (schema LE) + reportedUserId + 1 (sep) + reporterUserId + 1 (sep)
    //   + category + 1 (sep) + 32 (hash) + 1 (sep) + 8 (reportedAt LE)
    const totalLen =
      8 +
      reportedUserIdBytes.length +
      1 +
      reporterUserIdBytes.length +
      1 +
      categoryBytes.length +
      1 +
      32 +
      1 +
      8;

    const buf = new Uint8Array(totalLen);
    const dv = new DataView(buf.buffer);
    let off = 0;

    // schema as uint64 LE (schema is small so high bytes are 0)
    dv.setUint32(off, p.schema >>> 0, true);
    dv.setUint32(off + 4, 0, true);
    off += 8;

    // reportedUserId
    buf.set(reportedUserIdBytes, off);
    off += reportedUserIdBytes.length;
    buf[off++] = SEP;

    // reporterUserId
    buf.set(reporterUserIdBytes, off);
    off += reporterUserIdBytes.length;
    buf[off++] = SEP;

    // category
    buf.set(categoryBytes, off);
    off += categoryBytes.length;
    buf[off++] = SEP;

    // messageIdHash (32 bytes)
    buf.set(p.messageIdHash, off);
    off += 32;
    buf[off++] = SEP;

    // reportedAt as uint64 LE
    // reportedAt is a safe integer (Unix seconds), so we can use two
    // 32-bit writes.  Low 32 bits, then high 32 bits.
    const lo = p.reportedAt >>> 0;
    const hi = Math.floor(p.reportedAt / 0x100000000) >>> 0;
    dv.setUint32(off, lo, true);
    dv.setUint32(off + 4, hi, true);

    return buf;
  },

  // -------------------------------------------------------------------------
  // computeMessageIdHash
  // -------------------------------------------------------------------------

  /**
   * SHA-256 of the messageId UTF-8 bytes. Computed LOCALLY by the reporter
   * before the report leaves the device.
   *
   * Returns a 32-byte `Uint8Array`.
   *
   * Mirrors `SpamReportCodec.computeMessageIdHash` in iOS.
   */
  async computeMessageIdHash(messageId: string): Promise<Uint8Array> {
    const bytes = new TextEncoder().encode(messageId);
    const hashBuf = await crypto.subtle.digest("SHA-256", bytes);
    return new Uint8Array(hashBuf);
  },

  // -------------------------------------------------------------------------
  // sign
  // -------------------------------------------------------------------------

  /**
   * Signs `canonicalBytes(payload)` with the reporter's identity private key.
   *
   * `reporterIdentityPrivateKey` must be the raw 32-byte Ed25519 seed.
   *
   * Throws `SpamReportError` on bad lengths, self-report, or key errors.
   *
   * Mirrors `SpamReportCodec.sign` in iOS.
   */
  async sign(
    payload: SpamReportPayload,
    reporterIdentityPrivateKey: Uint8Array,
  ): Promise<SpamReport> {
    if (!payload.reportedUserId) {
      throw new SpamReportError({ tag: "emptyReportedUserId" });
    }
    if (!payload.reporterUserId) {
      throw new SpamReportError({ tag: "emptyReporterUserId" });
    }
    if (payload.reportedUserId === payload.reporterUserId) {
      throw new SpamReportError({ tag: "reporterEqualsReported" });
    }
    if (payload.messageIdHash.length !== 32) {
      throw new SpamReportError({
        tag: "invalidMessageIdHashLength",
        length: payload.messageIdHash.length,
      });
    }

    // Import the raw Ed25519 private key seed.
    // Web Crypto Ed25519 is available in modern browsers; the seed is the
    // 32-byte private key material in "raw" format for Ed25519 (PKCS#8 via
    // importKey with jwk format that embeds d+x, or via "raw" on supporting
    // implementations). We use the PKCS#8 import path here because it is the
    // widest-supported route in browsers that have Ed25519 support.
    const privateKey = await importEd25519PrivateKey(reporterIdentityPrivateKey);

    const canonical = SpamReportCodec.canonicalBytes(payload);
    const sigBuf = await crypto.subtle.sign("Ed25519", privateKey, canonical);
    const signature = new Uint8Array(sigBuf);

    return { payload, signature };
  },

  // -------------------------------------------------------------------------
  // verify
  // -------------------------------------------------------------------------

  /**
   * Constant-time Ed25519 verification against the supplied public key.
   * Returns false on any structural problem so callers can fail-closed.
   *
   * `reporterIdentityPublicKey` must be the raw 32-byte Ed25519 public key.
   *
   * Mirrors `SpamReportCodec.verify` in iOS.
   */
  async verify(
    report: SpamReport,
    reporterIdentityPublicKey: Uint8Array,
  ): Promise<boolean> {
    if (report.signature.length !== 64) return false;
    if (reporterIdentityPublicKey.length !== 32) return false;
    if (report.payload.messageIdHash.length !== 32) return false;

    try {
      const publicKey = await importEd25519PublicKey(reporterIdentityPublicKey);
      const canonical = SpamReportCodec.canonicalBytes(report.payload);
      return await crypto.subtle.verify(
        "Ed25519",
        publicKey,
        report.signature,
        canonical,
      );
    } catch {
      return false;
    }
  },

  // -------------------------------------------------------------------------
  // encode
  // -------------------------------------------------------------------------

  /**
   * Encodes a signed `SpamReport` to the wire format:
   *   `kaether:spam:report:v1:<base64url(JSON)>`
   *
   * Mirrors `SpamReportCodec.encode` in iOS.
   */
  encode(r: SpamReport): string {
    const json: SpamReportJson = {
      payload: {
        schema: r.payload.schema,
        reportedUserId: r.payload.reportedUserId,
        reporterUserId: r.payload.reporterUserId,
        category: r.payload.category,
        messageIdHash: uint8ArrayToBase64(r.payload.messageIdHash),
        reportedAt: r.payload.reportedAt,
      },
      signature: uint8ArrayToBase64(r.signature),
    };
    const jsonStr = JSON.stringify(json);
    return SpamReportCodec.PREFIX + base64UrlEncode(jsonStr);
  },

  // -------------------------------------------------------------------------
  // decode
  // -------------------------------------------------------------------------

  /**
   * Parses and validates schema + structural constraints.
   * Does NOT verify the signature — call `verify(report, publicKey)` after.
   *
   * Throws `SpamReportError` on any structural violation.
   *
   * Mirrors `SpamReportCodec.decode` in iOS.
   */
  decode(s: string): SpamReport {
    if (!s.startsWith(SpamReportCodec.PREFIX)) {
      throw new SpamReportError({
        tag: "malformed",
        detail: "not a spam report",
      });
    }
    const b64 = s.slice(SpamReportCodec.PREFIX.length);
    let jsonStr: string;
    try {
      jsonStr = base64UrlDecode(b64);
    } catch {
      throw new SpamReportError({
        tag: "malformed",
        detail: "invalid base64url",
      });
    }
    let json: SpamReportJson;
    try {
      json = JSON.parse(jsonStr) as SpamReportJson;
    } catch (e) {
      throw new SpamReportError({
        tag: "malformed",
        detail: `json parse failed: ${e}`,
      });
    }

    // Schema check
    if (json.payload.schema !== SPAM_REPORT_SCHEMA_VERSION) {
      throw new SpamReportError({
        tag: "unsupportedSchema",
        version: json.payload.schema,
      });
    }

    // Structural constraints
    if (!json.payload.reportedUserId) {
      throw new SpamReportError({ tag: "emptyReportedUserId" });
    }
    if (!json.payload.reporterUserId) {
      throw new SpamReportError({ tag: "emptyReporterUserId" });
    }
    if (json.payload.reportedUserId === json.payload.reporterUserId) {
      throw new SpamReportError({ tag: "reporterEqualsReported" });
    }

    const messageIdHash = base64ToUint8Array(json.payload.messageIdHash);
    if (messageIdHash.length !== 32) {
      throw new SpamReportError({
        tag: "invalidMessageIdHashLength",
        length: messageIdHash.length,
      });
    }

    const signature = base64ToUint8Array(json.signature);
    if (signature.length !== 64) {
      throw new SpamReportError({ tag: "invalidSignature" });
    }

    const payload: SpamReportPayload = {
      schema: json.payload.schema,
      reportedUserId: json.payload.reportedUserId,
      reporterUserId: json.payload.reporterUserId,
      category: json.payload.category,
      messageIdHash,
      reportedAt: json.payload.reportedAt,
    };

    return { payload, signature };
  },
} as const;

// ---------------------------------------------------------------------------
// Ed25519 key import helpers (Web Crypto API)
// ---------------------------------------------------------------------------

/**
 * Import a 32-byte Ed25519 private key seed as a `CryptoKey` for signing.
 * Uses the JWK import path (widest browser support for Ed25519).
 */
async function importEd25519PrivateKey(
  rawSeed: Uint8Array,
): Promise<CryptoKey> {
  // Convert raw 32-byte seed to JWK d field (base64url-encoded).
  const d = uint8ArrayToBase64Url(rawSeed);

  // We need the public key to build the JWK. For Ed25519 the public key is
  // derived from the seed; we do a temporary sign+extract or use PKCS#8.
  // The most portable approach on the web is PKCS#8 wrapping.
  // PKCS#8 for Ed25519: OID 1.3.101.112 = 06 03 2B 65 70.
  // PKCS#8 structure: SEQUENCE { INTEGER 0, SEQUENCE { OID }, OCTET STRING { OCTET STRING { seed } } }
  const pkcs8 = buildEd25519Pkcs8(rawSeed);

  return crypto.subtle.importKey(
    "pkcs8",
    pkcs8,
    { name: "Ed25519" },
    false,
    ["sign"],
  );
}

/**
 * Import a 32-byte Ed25519 public key as a `CryptoKey` for verification.
 */
async function importEd25519PublicKey(
  rawPub: Uint8Array,
): Promise<CryptoKey> {
  // SubjectPublicKeyInfo (SPKI) for Ed25519:
  // SEQUENCE { SEQUENCE { OID 1.3.101.112 }, BIT STRING { 0x00 || pubkey } }
  const spki = buildEd25519Spki(rawPub);

  return crypto.subtle.importKey(
    "spki",
    spki,
    { name: "Ed25519" },
    false,
    ["verify"],
  );
}

/**
 * Build a minimal PKCS#8 DER structure wrapping a raw 32-byte Ed25519 seed.
 *
 * Structure (per RFC 8410 / RFC 5958):
 *   SEQUENCE {
 *     INTEGER 0                      -- version
 *     SEQUENCE { OID 1.3.101.112 }   -- AlgorithmIdentifier
 *     OCTET STRING {                 -- privateKey
 *       OCTET STRING { <32-byte seed> }
 *     }
 *   }
 */
function buildEd25519Pkcs8(seed: Uint8Array): ArrayBuffer {
  // OID 1.3.101.112 encoded: 06 03 2B 65 70
  const oid = new Uint8Array([0x06, 0x03, 0x2b, 0x65, 0x70]);

  // AlgorithmIdentifier: SEQUENCE { OID }
  const algId = derSequence(oid);

  // Inner OCTET STRING wrapping the seed (CurvePrivateKey)
  const innerOctet = derOctetString(seed);
  // Outer OCTET STRING (PrivateKey field of OneAsymmetricKey)
  const outerOctet = derOctetString(innerOctet);

  // version INTEGER 0
  const version = new Uint8Array([0x02, 0x01, 0x00]);

  // Concatenate: version || algId || outerOctet
  const body = concatU8(version, algId, outerOctet);

  // Outer SEQUENCE
  const pkcs8 = derSequence(body);

  return pkcs8.buffer.slice(pkcs8.byteOffset, pkcs8.byteOffset + pkcs8.byteLength);
}

/**
 * Build a minimal SPKI DER structure for a raw 32-byte Ed25519 public key.
 *
 * Structure (per RFC 8410):
 *   SEQUENCE {
 *     SEQUENCE { OID 1.3.101.112 }   -- AlgorithmIdentifier
 *     BIT STRING { 0x00 || pubkey }  -- subjectPublicKey
 *   }
 */
function buildEd25519Spki(pub: Uint8Array): ArrayBuffer {
  const oid = new Uint8Array([0x06, 0x03, 0x2b, 0x65, 0x70]);
  const algId = derSequence(oid);

  // BIT STRING: 0x03, length, 0x00 (unused bits), pubkey bytes
  const bitStringContent = concatU8(new Uint8Array([0x00]), pub);
  const bitString = concatU8(
    new Uint8Array([0x03]),
    derLength(bitStringContent.length),
    bitStringContent,
  );

  const body = concatU8(algId, bitString);
  const spki = derSequence(body);

  return spki.buffer.slice(spki.byteOffset, spki.byteOffset + spki.byteLength);
}

function derSequence(contents: Uint8Array): Uint8Array {
  return concatU8(new Uint8Array([0x30]), derLength(contents.length), contents);
}

function derOctetString(contents: Uint8Array): Uint8Array {
  return concatU8(new Uint8Array([0x04]), derLength(contents.length), contents);
}

function derLength(len: number): Uint8Array {
  if (len < 0x80) return new Uint8Array([len]);
  if (len < 0x100) return new Uint8Array([0x81, len]);
  return new Uint8Array([0x82, (len >> 8) & 0xff, len & 0xff]);
}

function concatU8(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Base64 / base64url helpers
// ---------------------------------------------------------------------------

/** Standard base64 encode (with padding), matching iOS `Data.base64EncodedString()`. */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/** Standard base64 decode (with or without padding). */
function base64ToUint8Array(b64: string): Uint8Array {
  // Normalise padding
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/** Base64url (no padding). Used for the JWK `d` field. */
function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  return uint8ArrayToBase64(bytes)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** base64url-encode a UTF-8 string (for wire prefix payload). */
function base64UrlEncode(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** base64url-decode a string to UTF-8 text. */
function base64UrlDecode(s: string): string {
  let padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (padded.length % 4)) % 4;
  padded += "=".repeat(pad);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

// Re-export key generation helper for tests
export { importEd25519PrivateKey, importEd25519PublicKey };

/**
 * Generate a fresh Ed25519 keypair and return raw bytes.
 * Convenience for tests — not used in production code.
 */
export async function generateEd25519KeyPair(): Promise<{
  privateKeyBytes: Uint8Array;
  publicKeyBytes: Uint8Array;
}> {
  const keyPair = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  );
  const privJwk = await crypto.subtle.exportKey("jwk", keyPair.privateKey);
  const pubJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);

  // d and x are base64url-encoded 32-byte values
  const privateKeyBytes = base64UrlToUint8Array(privJwk.d!);
  const publicKeyBytes = base64UrlToUint8Array(pubJwk.x!);
  return { privateKeyBytes, publicKeyBytes };
}

function base64UrlToUint8Array(b64url: string): Uint8Array {
  let padded = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (padded.length % 4)) % 4;
  padded += "=".repeat(pad);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}
